import { useState, useEffect } from 'react';
import { Users, Shield, Calendar, Clock, UserCheck, UserX, AlertCircle, RefreshCw, Plus, CheckCircle, Mail, CreditCard, Package, MessageSquare, Wifi, WifiOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Função para normalizar texto (remover acentos)
const normalizeText = (text) => {
  if (!text) return '';
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
};

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/check`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          fetchUsers();
          fetchStats();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar admin:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setUsers(await response.json());
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleExtendSubscription = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/extend?days=${extendDays}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Assinatura estendida por ${extendDays} dias`);
        setShowExtendDialog(false);
        fetchUsers();
        fetchStats();
      } else {
        toast.error('Erro ao estender assinatura');
      }
    } catch (error) {
      toast.error('Erro ao estender assinatura');
    }
  };

  const handleUpdateStatus = async (userId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Status atualizado');
        fetchUsers();
        fetchStats();
      } else {
        toast.error('Erro ao atualizar status');
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = normalizeText(user.email).includes(normalizeText(searchTerm));
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && user.subscription_status === 'active') ||
      (filterStatus === 'trial' && user.subscription_status === 'trial') ||
      (filterStatus === 'expired' && user.days_remaining <= 0 && user.subscription_status !== 'active') ||
      (filterStatus === 'cancelled' && user.subscription_status === 'cancelled');
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return '-';
    }
  };

  const getStatusBadge = (user) => {
    if (user.days_remaining <= 0 && user.subscription_status !== 'active') {
      return 'px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-500/20 text-red-400 border border-red-500/30';
    }
    switch (user.subscription_status) {
      case 'active':
        return 'px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
      case 'trial':
        return 'px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30';
      case 'cancelled':
        return 'px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-500/20 text-gray-400 border border-gray-500/30';
      default:
        return 'px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  };

  const getStatusLabel = (user) => {
    if (user.days_remaining <= 0 && user.subscription_status !== 'active') {
      return 'Expirado';
    }
    switch (user.subscription_status) {
      case 'active': return 'Ativo';
      case 'trial': return 'Em Teste';
      case 'cancelled': return 'Cancelado';
      default: return user.subscription_status;
    }
  };

  const getDaysRemainingBadge = (days) => {
    if (days <= 0) {
      return 'text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400';
    } else if (days <= 7) {
      return 'text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400';
    } else {
      return 'text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400';
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-zinc-950">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen bg-zinc-950">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Shield className="w-16 h-16 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Acesso Negado</h2>
            <p className="text-slate-400">Você não tem permissão para acessar esta área.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="admin-page">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8 h-screen overflow-y-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-violet-500" />
            Painel Administrativo
          </h1>
          <p className="text-sm text-slate-400 mt-1">Gerencie usuários e assinaturas do sistema</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-violet-500/30 transition-colors">
              <Users className="w-6 h-6 text-violet-500 mb-2" />
              <p className="text-2xl font-bold text-white">{stats.total_users}</p>
              <p className="text-xs text-slate-400">Total Usuários</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-emerald-500/30 transition-colors">
              <UserCheck className="w-6 h-6 text-emerald-500 mb-2" />
              <p className="text-2xl font-bold text-emerald-500">{stats.active_users}</p>
              <p className="text-xs text-slate-400">Ativos</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-amber-500/30 transition-colors">
              <Clock className="w-6 h-6 text-amber-500 mb-2" />
              <p className="text-2xl font-bold text-amber-500">{stats.trial_users}</p>
              <p className="text-xs text-slate-400">Em Teste</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-red-500/30 transition-colors">
              <AlertCircle className="w-6 h-6 text-red-500 mb-2" />
              <p className="text-2xl font-bold text-red-500">{stats.expired_users}</p>
              <p className="text-xs text-slate-400">Expirados</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-gray-500/30 transition-colors">
              <UserX className="w-6 h-6 text-gray-500 mb-2" />
              <p className="text-2xl font-bold text-gray-500">{stats.cancelled_users}</p>
              <p className="text-xs text-slate-400">Cancelados</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-sky-500/30 transition-colors">
              <Package className="w-6 h-6 text-sky-500 mb-2" />
              <p className="text-2xl font-bold text-sky-500">{stats.total_clients}</p>
              <p className="text-xs text-slate-400">Clientes Total</p>
            </div>
          </div>
        )}

        {/* Search Bar - Sticky */}
        <div className="sticky top-4 z-30 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <svg 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <Input
                type="text"
                placeholder="Buscar por email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-700 text-white shadow-lg"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="trial">Em Teste</SelectItem>
                <SelectItem value="expired">Expirados</SelectItem>
                <SelectItem value="cancelled">Cancelados</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => { fetchUsers(); fetchStats(); }}
              variant="outline"
              className="border-zinc-700 text-slate-300 hover:bg-zinc-800"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Users Cards Grid */}
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-slate-400">Nenhum usuário encontrado</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((user) => (
              <div 
                key={user.id} 
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-violet-500/30 transition-colors"
                data-testid={`user-card-${user.id}`}
              >
                {/* Header do Card */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <h3 className="text-sm font-bold text-white truncate" title={user.email}>
                        {user.email}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <span className={getStatusBadge(user)}>
                        {getStatusLabel(user)}
                      </span>
                      <span className={getDaysRemainingBadge(user.days_remaining)}>
                        <Clock className="w-3 h-3 inline mr-1" />
                        {user.days_remaining} dias
                      </span>
                      {/* Status WhatsApp */}
                      <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                        user.whatsapp_connected 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {user.whatsapp_connected ? (
                          <><Wifi className="w-3 h-3" /> Online</>
                        ) : (
                          <><WifiOff className="w-3 h-3" /> Offline</>
                        )}
                      </span>
                      {/* Uptime do WhatsApp */}
                      {user.whatsapp_connected && user.whatsapp_uptime_seconds > 0 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-sky-500/20 text-sky-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatUptime(user.whatsapp_uptime_seconds)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Informações */}
                <div className="space-y-2 mb-4 pb-4 border-b border-zinc-800">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Clientes:</span>
                    <span className="text-white font-medium">{user.clients_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Mensagens:
                    </span>
                    <span className="text-white font-medium">{user.messages_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Cadastro:</span>
                    <span className="text-white">{formatDate(user.created_at)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Expira em:</span>
                    <span className={user.days_remaining <= 7 ? 'text-amber-400' : 'text-white'}>
                      {formatDate(user.trial_end)}
                    </span>
                  </div>
                </div>

                {/* Ações */}
                <div className="space-y-3">
                  <Button
                    onClick={() => { setSelectedUser(user); setShowExtendDialog(true); }}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Estender Assinatura
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Status:</span>
                    <Select
                      value={user.subscription_status}
                      onValueChange={(value) => handleUpdateStatus(user.id, value)}
                    >
                      <SelectTrigger className="flex-1 h-8 bg-zinc-800 border-zinc-700 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">Teste</SelectItem>
                        <SelectItem value="active">Ativo</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Extend Dialog */}
        <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-violet-500" />
                Estender Assinatura
              </DialogTitle>
            </DialogHeader>
            
            {selectedUser && (
              <div className="py-4 space-y-4">
                <div className="p-4 bg-zinc-800 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <p className="text-white font-medium">{selectedUser.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={getStatusBadge(selectedUser)}>
                      {getStatusLabel(selectedUser)}
                    </span>
                    <span className="text-slate-400 text-sm">
                      {selectedUser.days_remaining} dias restantes
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 block mb-3">Dias para adicionar:</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[7, 15, 30, 60, 90, 365].map((days) => (
                      <button
                        key={days}
                        onClick={() => setExtendDays(days)}
                        className={`px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                          extendDays === days
                            ? 'bg-violet-600 text-white'
                            : 'bg-zinc-800 text-slate-400 hover:bg-zinc-700'
                        }`}
                      >
                        {days} dias
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={() => setShowExtendDialog(false)}
                className="text-slate-400"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleExtendSubscription}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
